import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useState, type FormEvent } from "react";
import { ArrowLeft, Check, KeyRound, Shield, UserCog, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PasswordInput,
  PasswordRequirements,
  passwordMeetsRules,
} from "@/components/PasswordInput";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { deleteOwnAccount } from "@/lib/account.functions";
import {
  adminGetUserBasic,
  adminResetUserPassword,
} from "@/lib/admin.functions";
import { toast } from "sonner";

const profileSearchSchema = z.object({
  /** When set, super admin is viewing another user's profile in override mode. */
  actAs: fallback(z.string().uuid().optional(), undefined),
});

export const Route = createFileRoute("/_app/profile")({
  validateSearch: zodValidator(profileSearchSchema),
  component: ProfilePage,
  head: () => ({ meta: [{ title: "Profile — Bracket Challenge" }] }),
});

function ProfilePage() {
  const router = useRouter();
  const { profile, isAdmin } = useAuth();
  const { actAs } = Route.useSearch();

  // Override mode is only honored for super admins acting on someone else.
  const overrideUserId = isAdmin && actAs && actAs !== profile?.id ? actAs : null;

  if (overrideUserId) {
    return <AdminOverrideProfileView targetUserId={overrideUserId} />;
  }

  return <SelfProfileView />;
}

// ===================================================================
// Override view — admin viewing another user's identity
// ===================================================================

function AdminOverrideProfileView({ targetUserId }: { targetUserId: string }) {
  const router = useRouter();
  const [target, setTarget] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const getTargetFn = useAuthedServerFn(adminGetUserBasic);
  const resetPwFn = useAuthedServerFn(adminResetUserPassword);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getTargetFn({ data: { userId: targetUserId } });
        if (!cancelled) setTarget(res);
      } catch (e) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : "Failed to load user");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId]);

  const fullName = target
    ? `${target.firstName} ${target.lastName}`.trim() || target.email
    : "this member";

  const handleReset = async () => {
    setResetting(true);
    try {
      await resetPwFn({
        data: { userId: targetUserId, viaOverrideMode: true },
      });
      toast.success(`Password reset email sent to ${target?.email ?? fullName}`);
      setResetOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send reset");
    } finally {
      setResetting(false);
    }
  };

  return (
    <main className="max-w-md mx-auto px-4 py-8">
      <button
        type="button"
        onClick={() => {
          if (window.history.length > 1) router.history.back();
          else router.navigate({ to: "/home" });
        }}
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft className="w-3 h-3" /> Back
      </button>

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-700 dark:text-amber-300">
        <UserCog className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <div className="font-semibold">Override mode</div>
          <div>
            You are viewing {fullName}'s profile as a super admin. Sensitive
            actions (password reset, identity edits) are routed through the
            admin tools and audited.
          </div>
        </div>
      </div>

      <h1 className="font-display text-2xl font-bold text-foreground mb-6">
        {fullName}'s profile
      </h1>

      {loading ? (
        <div className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">
          Loading…
        </div>
      ) : !target ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive">
          Could not load this user.
        </div>
      ) : (
        <>
          <div className="space-y-4 rounded-xl border border-border bg-card p-5 mb-6">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First name</Label>
                <Input value={target.firstName} disabled />
              </div>
              <div>
                <Label>Last name</Label>
                <Input value={target.lastName} disabled />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input value={target.email} disabled />
            </div>

            <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground leading-snug">
              To change this member's name or email, use the Members admin
              page — those edits are logged separately.
            </div>

            <Button asChild variant="outline" size="sm">
              <Link to="/admin/members">
                <Shield className="w-3.5 h-3.5" />
                Edit identity in Members admin
              </Link>
            </Button>
          </div>

          <div className="space-y-3 rounded-xl border border-border bg-card p-5">
            <h2 className="font-display font-semibold text-foreground">
              Password
            </h2>
            <p className="text-xs text-muted-foreground leading-snug">
              For security, super admins can't set another user's password
              directly. Send a password reset email and the user can choose
              their own.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => setResetOpen(true)}
            >
              <KeyRound className="w-4 h-4" />
              Send password reset to {target.email}
            </Button>
          </div>
        </>
      )}

      <Dialog open={resetOpen} onOpenChange={(o) => !resetting && setResetOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send password reset?</DialogTitle>
            <DialogDescription>
              An email will be sent to{" "}
              <span className="font-mono">{target?.email}</span> with a link to
              set a new password. This action is recorded in the admin audit
              log.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setResetOpen(false)}
              disabled={resetting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleReset} disabled={resetting}>
              {resetting ? "Sending…" : "Send reset email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

// ===================================================================
// Self view — original profile page (unchanged behavior)
// ===================================================================

function SelfProfileView() {
  const router = useRouter();
  const { profile, isAdmin } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // password
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  // delete account
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteEmailInput, setDeleteEmailInput] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name);
      setLastName(profile.last_name);
    }
  }, [profile]);


  const savePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!passwordMeetsRules(newPw)) {
      return toast.error("Password doesn't meet all requirements yet.");
    }
    if (newPw !== confirmPw) return toast.error("Passwords don't match");
    setSavingPw(true);
    const { error } = await supabase.auth.updateUser({ password: newPw });
    setSavingPw(false);
    if (error) return toast.error(error.message);
    setNewPw(""); setConfirmPw("");
    toast.success("Password updated");
  };

  const pwAllPass = passwordMeetsRules(newPw);
  const pwsMatch = newPw.length > 0 && newPw === confirmPw;

  const userEmail = profile?.email ?? "";
  const emailMatches =
    deleteEmailInput.trim().toLowerCase() === userEmail.trim().toLowerCase() &&
    userEmail.length > 0;

  const handleDeleteAccount = async () => {
    if (!emailMatches) return;
    setDeleting(true);
    try {
      await deleteOwnAccount({ data: { emailConfirmation: deleteEmailInput } });
      await supabase.auth.signOut();
      toast.success("Your account has been deleted.");
      window.location.href = "/";
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to delete account.";
      toast.error(msg);
      setDeleting(false);
    }
  };

  return (
    <main className="max-w-md mx-auto px-4 py-8">
      <button
        type="button"
        onClick={() => {
          if (window.history.length > 1) router.history.back();
          else router.navigate({ to: "/home" });
        }}
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft className="w-3 h-3" /> Back
      </button>
      <h1 className="font-display text-2xl font-bold text-foreground mb-6">Your profile</h1>

      <div className="space-y-4 rounded-xl border border-border bg-card p-5 mb-6">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>First name</Label>
            <Input value={firstName} disabled />
          </div>
          <div>
            <Label>Last name</Label>
            <Input value={lastName} disabled />
          </div>
        </div>
        <div>
          <Label>Email</Label>
          <Input value={profile?.email || ""} disabled />
        </div>

        <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground leading-snug space-y-2">
          <p>
            Your name and email are locked. If something here is wrong, ask a
            league admin to fix it for you.
          </p>
          <p>
            Your <span className="font-semibold text-foreground">display name</span>{" "}
            and whether to show your real name are set per league when you build
            your bracket. Open any league to change those settings.
          </p>
        </div>
      </div>

      <form onSubmit={savePassword} className="space-y-4 rounded-xl border border-border bg-card p-5">
        <h2 className="font-display font-semibold text-foreground">Change password</h2>
        <div className="space-y-1.5">
          <Label>New password</Label>
          <PasswordInput
            autoComplete="new-password"
            required
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
          />
          {newPw.length > 0 && <PasswordRequirements value={newPw} className="mt-2" />}
        </div>
        <div className="space-y-1.5">
          <Label>Confirm</Label>
          <PasswordInput
            autoComplete="new-password"
            required
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
          />
          {confirmPw.length > 0 && (
            <p className={`flex items-center gap-1.5 text-xs ${pwsMatch ? "text-success" : "text-destructive"}`}>
              {pwsMatch ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <X className="w-3.5 h-3.5" />}
              {pwsMatch ? "Passwords match" : "Passwords don't match"}
            </p>
          )}
        </div>
        <Button type="submit" disabled={savingPw || !pwAllPass || !pwsMatch}>
          {savingPw ? "Saving…" : "Update password"}
        </Button>
      </form>

      {!isAdmin && (
        <div className="mt-6 space-y-3 rounded-xl border border-destructive/30 bg-destructive/5 p-5">
          <h2 className="font-display font-semibold text-destructive">Danger zone</h2>
          <p className="text-xs text-muted-foreground">
            Deleting your account permanently removes your profile, league memberships,
            brackets, and bonus picks. You won't be able to log in again unless you
            rejoin a league with a new invite code. This can't be undone.
          </p>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setDeleteEmailInput("");
              setDeleteOpen(true);
            }}
          >
            Delete my account
          </Button>
        </div>
      )}

      <Dialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (deleting) return;
          setDeleteOpen(open);
          if (!open) setDeleteEmailInput("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>
              This permanently deletes your profile, all league memberships, brackets,
              and bonus picks. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="confirm-email">
              Type your email <span className="font-mono">{userEmail}</span> to confirm:
            </Label>
            <Input
              id="confirm-email"
              autoComplete="off"
              value={deleteEmailInput}
              onChange={(e) => setDeleteEmailInput(e.target.value)}
              placeholder={userEmail}
              disabled={deleting}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={!emailMatches || deleting}
            >
              {deleting ? "Deleting…" : "Permanently delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
